import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Minus, FileText, Shield, CalendarClock, CreditCard, Eye, Mail, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ConsentRow({ label, status, description }) {
  // status: 'agreed' | 'declined' | 'unknown'
  const cfg = {
    agreed: { icon: Check, bg: "bg-emerald-50 border-emerald-200", iconBg: "bg-emerald-500", text: "text-emerald-700", badge: "동의" },
    declined: { icon: Minus, bg: "bg-stone-50 border-stone-200", iconBg: "bg-stone-400", text: "text-stone-600", badge: "비동의" },
    unknown: { icon: Minus, bg: "bg-amber-50 border-amber-200", iconBg: "bg-amber-400", text: "text-amber-700", badge: "기록 없음" },
  }[status] || cfg.unknown;
  const Icon = cfg.icon;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg}`}>
      <div className={`w-7 h-7 rounded-full ${cfg.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${cfg.text}`}>{label}</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cfg.iconBg} text-white`}>{cfg.badge}</span>
        </div>
        {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
      </div>
    </div>
  );
}

export default function UserConsentModal({ userId, token, onClose }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "사용자 정보를 불러오지 못했습니다.");
        setUser(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId, token]);

  // 동의 상태 결정 — 필수 동의는 가입 자체가 의미함
  const getRequiredStatus = (key) => {
    if (!user) return "unknown";
    if (user[key] === true) return "agreed";
    if (user[key] === false) return "declined";
    // 기존 회원: 필수 약관은 가입 자체로 동의 간주
    return user.created_at ? "agreed" : "unknown";
  };
  const getOptionalStatus = (key) => {
    if (!user) return "unknown";
    if (user[key] === true) return "agreed";
    if (user[key] === false) return "declined";
    return "unknown";
  };

  const recordedAt = user?.consent_recorded_at || user?.created_at;
  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return iso;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="user-consent-modal"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between bg-gradient-to-br from-burgundy-50 to-rose-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                <FileText className="w-5 h-5 text-burgundy-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-stone-900">회원 가입 동의 이력</h2>
                <p className="text-xs text-stone-500">분쟁/환불 대응 증빙 자료</p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="닫기"
              className="w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center text-stone-500"
              data-testid="consent-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-burgundy-500" />
              </div>
            )}
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
            )}
            {user && (
              <>
                {/* 회원 요약 */}
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-burgundy-500 text-white flex items-center justify-center font-bold">
                      {(user.username || user.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-stone-900 truncate">{user.username || user.name || "닉네임 없음"}</p>
                      <p className="text-xs text-stone-500 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-stone-600">
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5 text-stone-400" />
                      <span>가입: <strong className="text-stone-800">{formatDate(user.created_at)}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="w-3.5 h-3.5 text-stone-400" />
                      <span>동의 기록: <strong className="text-stone-800">{formatDate(recordedAt)}</strong></span>
                    </div>
                  </div>
                </div>

                {/* 필수 동의 */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wide flex items-center gap-1.5">
                    <Shield className="w-3 h-3" /> 필수 동의 (4종)
                  </p>
                  <ConsentRow
                    label="이용약관 동의"
                    status={getRequiredStatus("terms_consent")}
                    description="제1-9조 New Coupling 이용약관"
                  />
                  <ConsentRow
                    label="개인정보 수집·이용 동의"
                    status={getRequiredStatus("privacy_consent")}
                    description="결혼이력 등 선택 항목 처리 포함"
                  />
                  <ConsentRow
                    label="만 19세 이상 성인 확인"
                    status={getRequiredStatus("age_consent")}
                    description="성인 인증 자기확인"
                  />
                  <ConsentRow
                    label="결제·환불 약관 동의"
                    status={getOptionalStatus("payment_terms_consent")}
                    description="자동연장, 청약철회 7일, 이벤트 환불 일정"
                  />
                </div>

                {/* 선택 동의 */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wide flex items-center gap-1.5">
                    <Eye className="w-3 h-3" /> 선택 동의 (2종)
                  </p>
                  <ConsentRow
                    label="내 프로필 회원정보보기 공개"
                    status={getOptionalStatus("profile_visible_consent")}
                    description={
                      user.hidden_from_listing
                        ? "현재 비공개 상태"
                        : "현재 공개 상태"
                    }
                  />
                  <ConsentRow
                    label="마케팅 정보 수신 동의"
                    status={getOptionalStatus("marketing_consent")}
                    description="이벤트·프로모션·신규 서비스 이메일 수신"
                  />
                </div>

                {/* Legacy 회원 안내 */}
                {!user.consent_recorded_at && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 leading-relaxed flex items-start gap-2">
                    <CreditCard className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      이 회원은 결제·환불/프로필 공개 동의 항목이 도입되기 <strong>이전에 가입</strong>했습니다.
                      필수 약관(이용약관·개인정보·만 19세)은 가입 자체로 동의한 것으로 간주됩니다.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between text-[11px] text-stone-500">
            <span>본 기록은 분쟁·환불 시 증빙 자료로 활용됩니다.</span>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-stone-800 text-white text-xs font-medium hover:bg-stone-900"
            >
              닫기
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
